import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import {
  Card,
  Button,
  Input,
  Select,
  Table,
  Checkbox,
  DatePicker,
  Alert,
  Tag,
  AutoComplete,
  Tooltip,
  Spin,
  Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ReloadOutlined,
  FileTextOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  FilterOutlined,
  SettingOutlined,
  CloseOutlined,
  BarChartOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  UserOutlined,
  TruckOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import { PENDING_THRESHOLD_DAYS } from "../utils/constants";
import {
  useGetSummaryListQuery,
  useGetSummaryStatsQuery,
  useGetSummaryExportMutation,
} from "../store/api/summaryApi";
import type { SummaryRowResponse } from "@OrianaTypes";
import type { ListSummaryRequest } from "@OrianaTypes";
import { getPaymentStatusColor, getPoStatusColor } from "../utils";
import { formatLabel } from "../utils";
import { poStatusOptions } from "../utils/constants";
import { paymentStatusOptions } from "../utils/constants";
import { usePermission } from "../hooks/usePermission";
import { PERMISSIONS } from "../constants/permissions";
import { useDebounce } from "../hooks/useDebounce";
import { useGetClientsQuery } from "../store/api/clientApi";
import { useGetCategoriesQuery } from "../store/api/categoryApi";
import { useGetOEMsQuery } from "../store/api/oemApi";
import { useLazyGetFilteredProductsQuery } from "../store/api/productApi";
import { useGetUsersQuery } from "../store/api/userApi";
import SummaryDetailModal from "../Components/SummaryDetailModal";
import "./SummaryDashboard.css";

/** Tooltip only when table cell text is truncated (ellipsis), not on every cell */
const EllipsisTooltipText: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  const inner = (
    <span ref={ref} className="summary-table-cell-tooltip-wrap">
      {text}
    </span>
  );
  if (!overflow || !text || text === "-") return inner;
  return (
    <Tooltip title={text} placement="topLeft" mouseEnterDelay={0.15}>
      {inner}
    </Tooltip>
  );
};

const PoIdLinkCell: React.FC<{
  poId: string;
  row: SummaryRowResponse;
  onOpen: (r: SummaryRowResponse) => void;
}> = ({ poId, row, onOpen }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [poId]);
  const btn = (
    <Button type="link" className="summary-po-link-btn" onClick={() => onOpen(row)}>
      <span ref={ref} className="summary-table-cell-tooltip-wrap">
        {poId}
      </span>
    </Button>
  );
  if (!overflow) return btn;
  return <Tooltip title={poId} mouseEnterDelay={0.15}>{btn}</Tooltip>;
};

const SUMMARY_COLUMNS_STORAGE_KEY = "summaryDashboardColumns";

const PIPELINE_STAGES: { value: string; label: string }[] = [
  { value: "dispatch", label: "Dispatch" },
  { value: "document", label: "Document" },
  { value: "delivery", label: "Delivery" },
  { value: "preCommissioning", label: "Pre-Commissioning" },
  { value: "commissioning", label: "Commissioning" },
  { value: "warranty", label: "Warranty" },
];

const PIPELINE_STATUS_OPTIONS = [
  { value: "Not Started", label: "Not Started" },
  { value: "In-Progress", label: "In-Progress" },
  { value: "Done", label: "Done" },
];

/** Default columns shown when none are stored (Order ID, Date, Sales Person, etc.) */
const DEFAULT_SELECTED_COLUMNS = [
  "poId",
  "clientPoDate",
  "createdByName",
  "clientName",
  "clientPoNo",
  "paymentStatus",
  "categoryNames",
  "oemNames",
  "productNames",
  "totalQuantity",
  "totalPrice",
  "finalPrice",
  "confirmDateOfDispatch",
];

const ALL_COLUMN_KEYS = [
  // Basic Info
  "poId",
  "clientPoDate",
  "createdByName",
  "clientName",
  "osgPiNo",
  "osgPiDate",
  // PO Details
  "poStatus",
  "clientPoNo",
  "paymentStatus",
  "noOfDispatch",
  // Products
  "categoryNames",
  "oemNames",
  "productNames",
  "totalQuantity",
  "totalPrice",
  "finalPrice",
  "amountReceived",
  "remainingAmount",
  // Dispatch
  "dispatchPlanDate",
  "confirmDateOfDispatch",
  "siteLocation",
  "oscSupport",
  "assignedUserName",
  "assignedServiceUserName",
  "dispatchedQuantity",
  "balanceQuantity",
  "dispatchPercentage",
  "taxInvoiceNumber",
  "invoiceDate",
  "dispatchDate",
  "dispatchStatus",
  // Delivery
  "deliveredQuantity",
  "deliveryBalanceQuantity",
  "deliveryPercentage",
  "deliveryPincode",
  "deliveryStatus",
  // Service
  "ppmConfirmationStatus",
  "preCommissioningStatus",
  "commissioningEcdFromClient",
  "assignedServiceEngineer",
  "commissioningDate",
  "commissioningStatus",
  "commissioningQuantity",
  "commissioningBalanceQuantity",
  "commissioningPercentage",
  "warrantyQuantity",
  "warrantyBalanceQuantity",
  "warrantyPercentage",
  "warrantyStatus",
  // Other
  "remarks",
];

const COLUMN_TITLES: Record<string, string> = {
  // Basic Info
  poId: "Order ID",
  clientPoDate: "Date",
  createdByName: "Sales Person",
  clientName: "Client",
  osgPiNo: "OSG PI No",
  osgPiDate: "OSG PI Date",
  // PO Details
  poStatus: "Status",
  clientPoNo: "Client PO No",
  paymentStatus: "Payment Status",
  noOfDispatch: "No. of Dispatch",
  // Products
  categoryNames: "Category",
  oemNames: "OEM",
  productNames: "Product",
  totalQuantity: "Total Quantity",
  totalPrice: "Total Price",
  finalPrice: "Final Price",
  amountReceived: "Amount Received",
  remainingAmount: "Remaining Amount",
  // Dispatch
  dispatchPlanDate: "Dispatch Plan Date",
  confirmDateOfDispatch: "Confirm Dispatch Date",
  siteLocation: "Delivery Location",
  oscSupport: "OSC Support",
  assignedUserName: "Assigned to (Dispatch)",
  assignedServiceUserName: "Assigned to (Service)",
  dispatchedQuantity: "Dispatched Qty",
  balanceQuantity: "Balance Qty",
  dispatchPercentage: "% Dispatch Done",
  taxInvoiceNumber: "Tax Invoice",
  invoiceDate: "Invoice Date",
  dispatchDate: "Dispatch Date",
  dispatchStatus: "Dispatch Status",
  // Delivery
  deliveredQuantity: "Delivery Qty",
  deliveryBalanceQuantity: "Balance Qty (Delivery)",
  deliveryPercentage: "% Delivery Done",
  deliveryPincode: "Material Dispatch From",
  deliveryStatus: "Delivery Status",
  // Service
  ppmConfirmationStatus: "PPM Confirmation Status",
  preCommissioningStatus: "Pre-Commissioning Status",
  commissioningEcdFromClient: "ECD from Client",
  assignedServiceEngineer: "Assigned Engineer",
  commissioningDate: "Commissioning Date",
  commissioningStatus: "Commissioning Status",
  commissioningQuantity: "Commissioning Qty",
  commissioningBalanceQuantity: "Balance Qty (Commissioning)",
  commissioningPercentage: "% Commissioning Done",
  warrantyQuantity: "Warranty Qty",
  warrantyBalanceQuantity: "Balance Qty (Warranty)",
  warrantyPercentage: "% Warranty Done",
  warrantyStatus: "Warranty Status",
  // Other
  remarks: "Remarks",
};

const SummaryDashboard: React.FC = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [poId, setPoId] = useState<string>("");
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");
  const [selectedClient, setSelectedClient] = useState<{
    clientId: number;
    clientName: string;
  } | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | undefined>();
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [pipelineStage, setPipelineStage] = useState<string | undefined>();
  const [pipelineStatus, setPipelineStatus] = useState<string | undefined>();

  // New filter states - Cascading: Category → OEM → Product
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedOEM, setSelectedOEM] = useState<number | undefined>();
  const [selectedProduct, setSelectedProduct] = useState<number | undefined>();
  const [selectedSalesman, setSelectedSalesman] = useState<number | undefined>();
  const [dispatchId, setDispatchId] = useState<string>("");
  const [serviceId, setServiceId] = useState<string>("");
  const [taxInvoice, setTaxInvoice] = useState<string>("");

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SUMMARY_COLUMNS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // ignore
    }
    return [...DEFAULT_SELECTED_COLUMNS];
  });

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Detail modal state
  const [selectedPO, setSelectedPO] = useState<SummaryRowResponse | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SUMMARY_COLUMNS_STORAGE_KEY, JSON.stringify(selectedColumns));
    } catch {
      // ignore
    }
  }, [selectedColumns]);

  const canViewSummary = usePermission(PERMISSIONS.PO_READ_ALL);

  const debouncedClientSearch = useDebounce(clientSearchTerm, 500);
  const shouldFetchClients = debouncedClientSearch.length >= 3;
  const { data: clientsResponse } = useGetClientsQuery(
    { searchTerm: debouncedClientSearch, isActive: true },
    { skip: !shouldFetchClients }
  );
  const clientOptions = useMemo(() => {
    const data = clientsResponse?.data ?? [];
    return data.map((c) => ({
      value: c.clientName,
      label: c.clientName,
      clientId: c.clientId,
    }));
  }, [clientsResponse]);

  // Fetch Categories and OEMs (always loaded)
  const { data: categoriesResponse } = useGetCategoriesQuery();
  const { data: oemsResponse } = useGetOEMsQuery();

  const categoryOptions = useMemo(() => {
    const data = categoriesResponse?.data ?? [];
    return data
      .filter((cat) => cat.isActive)
      .map((cat) => ({
        value: cat.categoryId,
        label: cat.categoryName,
      }));
  }, [categoriesResponse]);

  const oemOptions = useMemo(() => {
    const data = oemsResponse?.data ?? [];
    return data
      .filter((oem) => oem.isActive)
      .map((oem) => ({
        value: oem.oemId,
        label: oem.name,
      }));
  }, [oemsResponse]);

  // Lazy query for products (only called when both Category and OEM are selected)
  const [fetchProducts, { data: productsData, isLoading: productsLoading }] =
    useLazyGetFilteredProductsQuery();

  const productOptions = useMemo(() => {
    if (!productsData || !selectedCategory || !selectedOEM) return [];
    return productsData
      .filter((prod) => prod.isActive)
      .map((prod) => ({
        value: prod.productId,
        label: prod.productName,
      }));
  }, [productsData, selectedCategory, selectedOEM]);

  // Fetch products when Category and OEM are both selected
  useEffect(() => {
    if (selectedCategory && selectedOEM) {
      fetchProducts({
        categoryId: selectedCategory,
        oemId: selectedOEM,
        isActive: true,
      });
    } else {
      setSelectedProduct(undefined);
    }
  }, [selectedCategory, selectedOEM, fetchProducts]);

  // Fetch Users for Salesman filter
  const { data: usersResponse } = useGetUsersQuery({
    page: 1,
    limit: 100,
    sortBy: "username",
    sortOrder: "ASC",
  });

  const salesmanOptions = useMemo(() => {
    const data = usersResponse?.data ?? [];
    return data
      .filter((user) => user.isActive)
      .map((user) => ({
        value: user.userId,
        label: user.username,
      }));
  }, [usersResponse]);

  const listParams: ListSummaryRequest = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      ...(poId && { searchKey: "poId", searchTerm: poId }),
      ...(selectedClient && { clientId: selectedClient.clientId }),
      ...(orderStatus && { poStatus: orderStatus }),
      ...(paymentStatus && { paymentStatus }),
      ...(startDate && { dateFrom: startDate.format("YYYY-MM-DD") }),
      ...(endDate && { dateTo: endDate.format("YYYY-MM-DD") }),
      ...(pipelineStage && { pipelineStage }),
      ...(pipelineStatus && { pipelineStatus }),
      ...(selectedCategory && { categoryId: selectedCategory }),
      ...(selectedOEM && { oemId: selectedOEM }),
      ...(selectedProduct && { productId: selectedProduct }),
      ...(selectedSalesman && { salesmanId: selectedSalesman }),
      ...(dispatchId && { dispatchId: parseInt(dispatchId, 10) }),
      ...(serviceId && { serviceId: parseInt(serviceId, 10) }),
      ...(taxInvoice && { taxInvoice }),
    }),
    [
      currentPage,
      pageSize,
      poId,
      selectedClient,
      orderStatus,
      paymentStatus,
      startDate,
      endDate,
      pipelineStage,
      pipelineStatus,
      selectedCategory,
      selectedOEM,
      selectedProduct,
      selectedSalesman,
      dispatchId,
      serviceId,
      taxInvoice,
    ]
  );

  const statsParams = useMemo(
    () => ({
      ...listParams,
      page: undefined,
      limit: undefined,
    }),
    [listParams]
  );

  const {
    data: listData,
    isLoading: listLoading,
    isError: listError,
    error: listErrorDetail,
    refetch: refetchList,
  } = useGetSummaryListQuery(canViewSummary ? listParams : undefined, {
    skip: !canViewSummary,
  });

  const { data: statsData, isLoading: statsLoading } = useGetSummaryStatsQuery(
    canViewSummary ? statsParams : undefined,
    { skip: !canViewSummary }
  );

  const [exportCsv, { isLoading: exportLoading }] = useGetSummaryExportMutation();

  const handleRefresh = () => {
    setPoId("");
    setClientSearchTerm("");
    setSelectedClient(null);
    setOrderStatus(undefined);
    setPaymentStatus(undefined);
    setStartDate(null);
    setEndDate(null);
    setPipelineStage(undefined);
    setPipelineStatus(undefined);
    setSelectedCategory(undefined);
    setSelectedOEM(undefined);
    setSelectedProduct(undefined);
    setSelectedSalesman(undefined);
    setDispatchId("");
    setServiceId("");
    setTaxInvoice("");
    setCurrentPage(1);
    refetchList();
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleClearFilters = () => {
    setPoId("");
    setClientSearchTerm("");
    setSelectedClient(null);
    setOrderStatus(undefined);
    setPaymentStatus(undefined);
    setStartDate(null);
    setEndDate(null);
    setPipelineStage(undefined);
    setPipelineStatus(undefined);
    setSelectedCategory(undefined);
    setSelectedOEM(undefined);
    setSelectedProduct(undefined);
    setSelectedSalesman(undefined);
    setDispatchId("");
    setServiceId("");
    setTaxInvoice("");
    setCurrentPage(1);
    setShowFilter(false);
    refetchList();
  };

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (poId) count++;
    if (selectedClient) count++;
    if (orderStatus) count++;
    if (paymentStatus) count++;
    if (startDate || endDate) count++;
    if (pipelineStage || pipelineStatus) count++;
    if (selectedCategory) count++;
    if (selectedOEM) count++;
    if (selectedProduct) count++;
    if (selectedSalesman) count++;
    if (dispatchId) count++;
    if (serviceId) count++;
    if (taxInvoice) count++;
    return count;
  }, [
    poId,
    selectedClient,
    orderStatus,
    paymentStatus,
    startDate,
    endDate,
    pipelineStage,
    pipelineStatus,
    selectedCategory,
    selectedOEM,
    selectedProduct,
    selectedSalesman,
    dispatchId,
    serviceId,
    taxInvoice,
  ]);

  const handleExport = async () => {
    try {
      const { page: _page, ...exportParams } = listParams;
      const csv = await exportCsv({
        ...exportParams,
        columns: selectedColumns.join(","),
      }).unwrap();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-summary-${moment().format("YYYY-MM-DD")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Error handled by mutation
    }
  };

  // ── Dynamic column width constants ──────────────────────────────────────────
  // Average character width at Ant Design's default 13px table font
  const COL_HEADER_CHAR_W = 7.8; // header font is slightly larger / bolder
  const COL_DATA_CHAR_W   = 7.2; // body font
  const COL_HEADER_PAD    = 28;  // left + right cell padding in header
  const COL_DATA_PAD      = 20;  // left + right cell padding in body

  /**
   * Returns the estimated display character count for a single cell.
   * Used to find the longest value across all rows so we can auto-size the column.
   * Returns a char-count, NOT pixels. Caller multiplies by COL_DATA_CHAR_W.
   */
  const getCellDisplayLength = (key: string, row: SummaryRowResponse): number => {
    // Accordion status cells: "Not Started 0/0" → ~16 chars worst case
    if (
      key === "deliveryStatus" ||
      key === "preCommissioningStatus" ||
      key === "commissioningStatus" ||
      key === "warrantyStatus"
    ) return 18;

    // Dispatch status: Tag label only
    if (key === "dispatchStatus") {
      const s = row.dispatches?.[0]?.dispatchStatus;
      return s ? s.length + 4 : 0;
    }

    // Computed balance quantities — mimic the render formula
    if (key === "deliveryBalanceQuantity") {
      const b = Math.max(0, (row.dispatchedQuantity || 0) - (row.deliveredQuantity || 0));
      return b.toLocaleString("en-IN").length;
    }
    if (key === "commissioningBalanceQuantity") {
      const b = Math.max(0, (row.dispatchedQuantity || 0) - (row.commissioningQuantity || 0));
      return b.toLocaleString("en-IN").length;
    }
    if (key === "warrantyBalanceQuantity") {
      const b = Math.max(0, (row.commissioningQuantity || 0) - (row.warrantyQuantity || 0));
      return b.toLocaleString("en-IN").length;
    }

    // Per-service columns: single value or "Multiple" badge
    if (
      key === "ppmConfirmationStatus" ||
      key === "commissioningEcdFromClient" ||
      key === "assignedServiceEngineer" ||
      key === "commissioningDate"
    ) {
      const services = row.services ?? [];
      const unique = Array.from(
        new Set(
          services
            .map((s) => (s as unknown as Record<string, string | undefined>)[key])
            .filter((v): v is string => !!v)
        )
      );
      if (unique.length === 0) return 1;
      if (unique.length === 1) return unique[0].length;
      return 8; // "Multiple"
    }

    // Direct field access — covers most remaining keys
    const raw = (row as unknown as Record<string, unknown>)[key];
    if (raw == null) return 1;
    if (Array.isArray(raw)) return raw.join(", ").length;
    if (typeof raw === "number") {
      if (
        key === "totalPrice" ||
        key === "finalPrice" ||
        key === "amountReceived" ||
        key === "remainingAmount"
      ) return `₹${raw.toLocaleString("en-IN")}`.length;
      if (key.includes("Percentage")) return 6; // "100.0%"
      return raw.toLocaleString("en-IN").length;
    }
    if (typeof raw === "string") return raw.length;
    return String(raw).length;
  };

  /**
   * Per-content-type minimum widths (pixels) so very short actual values
   * (e.g. "0", "-") don't collapse a column to an unusable size.
   * Key fields get higher floors so Order ID, dates, and names stay readable.
   */
  const getContentMin = (key: string): number => {
    if (key === "poId") return 220;
    if (key === "clientName") return 160;
    if (key === "createdByName") return 150;
    if (key === "osgPiNo" || key === "clientPoNo") return 150;
    if (key === "confirmDateOfDispatch" || key === "dispatchPlanDate") return 175;
    if (key === "remarks") return 200;
    if (
      key === "deliveryStatus" ||
      key === "preCommissioningStatus" ||
      key === "commissioningStatus" ||
      key === "warrantyStatus" ||
      key === "dispatchStatus"
    )
      return 155;
    if (key.includes("Percentage")) return 100;
    if (key.includes("Date") || key === "osgPiDate" || key === "clientPoDate") return 118;
    if (key.includes("Quantity") || key === "totalQuantity") return 88;
    if (
      key === "totalPrice" ||
      key === "finalPrice" ||
      key === "amountReceived" ||
      key === "remainingAmount"
    )
      return 130;
    if (key === "categoryNames" || key === "oemNames" || key === "productNames") return 140;
    return 112;
  };

  /**
   * Memoized map of key → pixel width, recalculated whenever the page data changes.
   * For each column: max(header text width, widest data value width, content-type min).
   * ~50 cols × page-size rows is trivially fast (< 1 ms).
   */
  const columnWidths = useMemo<Record<string, number>>(() => {
    const data = listData?.data ?? [];
    const widths: Record<string, number> = {};

    for (const key of ALL_COLUMN_KEYS) {
      const title   = COLUMN_TITLES[key] ?? key;
      const headerW = Math.ceil(title.length * COL_HEADER_CHAR_W) + COL_HEADER_PAD;

      const maxDataChars = data.reduce(
        (max, row) => Math.max(max, getCellDisplayLength(key, row)),
        0
      );
      const dataW = Math.ceil(maxDataChars * COL_DATA_CHAR_W) + COL_DATA_PAD;

      widths[key] = Math.max(headerW, dataW, getContentMin(key));
    }

    return widths;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData?.data]);

  // Renders an accordionStatus stage as a colored Tag + X/Y count pill
  const renderAccordionStatusCell = (
    status: string,
    completed: number,
    total: number
  ) => {
    const allDone = completed >= total && total > 0;
    const notStarted = completed === 0;
    let tagColor = "default";
    if (status === "Done") tagColor = "success";
    else if (status === "In-Progress") tagColor = "warning";
    const pillClass = allDone
      ? "summary-status-pill summary-status-pill--done"
      : notStarted
        ? "summary-status-pill summary-status-pill--none"
        : "summary-status-pill summary-status-pill--partial";
    return (
      <span className="summary-status-cell">
        <Tag color={tagColor} className="summary-status-tag">{formatLabel(status)}</Tag>
        {total > 0 && (
          <span className={pillClass}>{completed}/{total}</span>
        )}
      </span>
    );
  };

  const tableColumns: ColumnsType<SummaryRowResponse> = useMemo(() => {
    const cols: ColumnsType<SummaryRowResponse> = [];
    const keysToShow = selectedColumns.filter((k) => ALL_COLUMN_KEYS.includes(k));
    keysToShow.forEach((key) => {
      const title = COLUMN_TITLES[key] ?? key;
      cols.push({
        title,
        key,
        dataIndex: key,
        ellipsis: { showTitle: false },
        width: columnWidths[key] ?? 150,
        render: (val: unknown, row: SummaryRowResponse) => {
          // ── Accordion-backed status columns: use aggregated accordionStatus ──
          if (key === "deliveryStatus") {
            const acc = row.accordionStatus?.delivery;
            if (!acc) return "-";
            return renderAccordionStatusCell(acc.status, acc.completed, acc.total);
          }
          if (key === "preCommissioningStatus") {
            const acc = row.accordionStatus?.preCommissioning;
            if (!acc) return "-";
            return renderAccordionStatusCell(acc.status, acc.completed, acc.total);
          }
          if (key === "commissioningStatus") {
            const acc = row.accordionStatus?.commissioning;
            if (!acc) return "-";
            return renderAccordionStatusCell(acc.status, acc.completed, acc.total);
          }
          if (key === "warrantyStatus") {
            const acc = row.accordionStatus?.warranty;
            if (!acc) return "-";
            return renderAccordionStatusCell(acc.status, acc.completed, acc.total);
          }

          // ── Per-service columns: show single value or "Multiple" badge ──
          if (
            key === "ppmConfirmationStatus" ||
            key === "commissioningEcdFromClient" ||
            key === "assignedServiceEngineer" ||
            key === "commissioningDate"
          ) {
            const services = row.services ?? [];
            if (services.length === 0) return "-";
            const values = services
              .map((s) => {
                const sv = s as unknown as Record<string, string | undefined>;
                return sv[key] ?? null;
              })
              .filter((v): v is string => v != null && v !== "");
            if (values.length === 0) return "-";
            const unique = Array.from(new Set(values));
            if (unique.length === 1) {
              const v = unique[0];
              if (key === "commissioningDate") {
                const d = moment(v).format("DD-MM-YYYY");
                return <EllipsisTooltipText text={d} />;
              }
              if (key === "commissioningEcdFromClient") {
                const d = moment(v).format("DD-MM-YYYY");
                return <EllipsisTooltipText text={d} />;
              }
              if (key === "ppmConfirmationStatus") {
                const t = formatLabel(v);
                return <Tag>{t}</Tag>;
              }
              return <EllipsisTooltipText text={v} />;
            }
            // Multiple distinct values — show badge with tooltip
            const tooltipText = unique
              .map((v) =>
                key === "commissioningDate" || key === "commissioningEcdFromClient"
                  ? moment(v).format("DD-MM-YYYY")
                  : v
              )
              .join(" · ");
            return (
              <Tooltip title={tooltipText}>
                <Tag className="summary-multi-tag">Multiple</Tag>
              </Tooltip>
            );
          }

          // ── Dispatch-level columns: read from dispatches[0] when top-level is null ──
          let effectiveVal: unknown = val;
          if (effectiveVal == null) {
            const firstDispatch = row.dispatches?.[0];
            if (key === "taxInvoiceNumber") effectiveVal = firstDispatch?.taxInvoiceNumber;
            if (key === "invoiceDate") effectiveVal = firstDispatch?.invoiceDate;
            if (key === "dispatchDate") effectiveVal = firstDispatch?.dispatchDate;
            if (key === "deliveryPincode") effectiveVal = firstDispatch?.deliveryPincode;
            if (key === "dispatchStatus") effectiveVal = firstDispatch?.dispatchStatus;
          }

          // ── Standard renders ──
          if (key === "poStatus" && typeof effectiveVal === "string") {
            const t = formatLabel(effectiveVal);
            return <Tag color={getPoStatusColor(effectiveVal)}>{t}</Tag>;
          }
          if (key === "paymentStatus" && typeof effectiveVal === "string") {
            const t = formatLabel(effectiveVal);
            return <Tag color={getPaymentStatusColor(effectiveVal)}>{t}</Tag>;
          }
          if (key === "dispatchStatus" && typeof effectiveVal === "string") {
            const t = formatLabel(effectiveVal);
            return <Tag>{t}</Tag>;
          }

          if (
            (key === "oemNames" || key === "categoryNames" || key === "productNames") &&
            Array.isArray(effectiveVal)
          ) {
            const joined = effectiveVal.length > 0 ? effectiveVal.join(", ") : "-";
            return <EllipsisTooltipText text={joined} />;
          }

          if (key.includes("Percentage") && typeof effectiveVal === "number") {
            const percentage = effectiveVal.toFixed(1);
            const pctStr = `${percentage}%`;
            let pctClass = "summary-pct-red";
            if (effectiveVal >= 100) pctClass = "summary-pct-green";
            else if (effectiveVal > 0) pctClass = "summary-pct-yellow";
            return <span className={pctClass}>{pctStr}</span>;
          }

          if (
            (key.includes("Date") || key === "osgPiDate" || key === "clientPoDate") &&
            typeof effectiveVal === "string"
          ) {
            const d = moment(effectiveVal).format("DD-MM-YYYY");
            return <EllipsisTooltipText text={d} />;
          }

          if (
            key === "totalPrice" ||
            key === "finalPrice" ||
            key === "amountReceived" ||
            key === "remainingAmount"
          ) {
            const num = Number(effectiveVal);
            if (!Number.isNaN(num)) {
              const cur = `₹${num.toLocaleString("en-IN")}`;
              return <EllipsisTooltipText text={cur} />;
            }
          }

          if (
            (key.includes("Quantity") || key === "totalQuantity") &&
            typeof effectiveVal === "number"
          ) {
            const q = effectiveVal.toLocaleString("en-IN");
            return <EllipsisTooltipText text={q} />;
          }

          if (key === "deliveryBalanceQuantity") {
            const balance = Math.max(0, (row.dispatchedQuantity || 0) - (row.deliveredQuantity || 0));
            const q = balance.toLocaleString("en-IN");
            return <EllipsisTooltipText text={q} />;
          }
          if (key === "commissioningBalanceQuantity") {
            const balance = Math.max(0, (row.dispatchedQuantity || 0) - (row.commissioningQuantity || 0));
            const q = balance.toLocaleString("en-IN");
            return <EllipsisTooltipText text={q} />;
          }
          if (key === "warrantyBalanceQuantity") {
            const balance = Math.max(0, (row.commissioningQuantity || 0) - (row.warrantyQuantity || 0));
            const q = balance.toLocaleString("en-IN");
            return <EllipsisTooltipText text={q} />;
          }

          if (key === "poId") {
            return (
              <PoIdLinkCell
                poId={effectiveVal as string}
                row={row}
                onOpen={(r) => {
                  setSelectedPO(r);
                  setShowDetailModal(true);
                }}
              />
            );
          }

          if (effectiveVal != null) {
            return <EllipsisTooltipText text={String(effectiveVal)} />;
          }
          return "-";
        },
      });
    });
    return cols;
  }, [selectedColumns, columnWidths]);

  // Total horizontal scroll width — forces Ant Design to use table-layout: fixed,
  // which respects exact column widths and eliminates header/data misalignment.
  const totalScrollX = useMemo(() => {
    const visibleKeys = selectedColumns.filter((k) => ALL_COLUMN_KEYS.includes(k));
    const sum = visibleKeys.reduce((acc, k) => acc + (columnWidths[k] ?? 150), 0);
    return sum + 64; // expand (+) column — reserve space so icon never shows ellipsis
  }, [selectedColumns, columnWidths]);

  const dataSource = useMemo(
    () => (listData?.data ?? []).map((row) => ({ ...row, key: row.poId })),
    [listData]
  );

  const stats = useMemo(
    () => [
      {
        title: "Total Orders",
        value: statsData?.total ?? 0,
        subtitle: "All orders",
        icon: <FileTextOutlined style={{ fontSize: 22, color: "#fff" }} />,
        accentColor: "#667eea",
        accentColor2: "#764ba2",
      },
      {
        title: "In Progress",
        value: statsData?.inProgress ?? 0,
        subtitle: "Not closed, by stage",
        icon: <SyncOutlined style={{ fontSize: 22, color: "#fff" }} />,
        accentColor: "#f7971e",
        accentColor2: "#ffd200",
      },
      {
        title: "Pending",
        value: statsData?.pending ?? 0,
        subtitle: `Past ${PENDING_THRESHOLD_DAYS} days`,
        icon: <ExclamationCircleOutlined style={{ fontSize: 22, color: "#fff" }} />,
        accentColor: "#f85f7a",
        accentColor2: "#fc466b",
        tooltip: `Orders not closed within ${PENDING_THRESHOLD_DAYS} days of PO received date. Requires attention.`,
      },
      {
        title: "Completed",
        value: statsData?.completed ?? 0,
        subtitle: "Closed orders",
        icon: <CheckCircleOutlined style={{ fontSize: 22, color: "#fff" }} />,
        accentColor: "#11998e",
        accentColor2: "#38ef7d",
      },
    ],
    [statsData]
  );

  const poStatusFilterOptions = [
    ...poStatusOptions,
    { value: "closed", label: "Closed" },
  ];

  if (!canViewSummary) {
    return (
      <div className="summary-access-wrap">
        <Alert
          type="warning"
          message="Access restricted"
          description="You need permission to view the summary dashboard. Contact your administrator."
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="summary-dashboard">
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      >
        <Card bordered={false} className="summary-hero-card">
          <div className="summary-hero-left">
            <div className="summary-hero-icon-wrap">
              <BarChartOutlined />
            </div>
            <div>
              <h2 className="summary-hero-title">Summary Dashboard</h2>
              <p className="summary-hero-subtitle">Track all purchase orders at a glance</p>
            </div>
          </div>
          <div className="summary-hero-actions">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exportLoading}
              className="summary-btn-export"
            >
              Export CSV
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              className="summary-btn-refresh"
            >
              Refresh
            </Button>
          </div>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.05, ease: [0.4, 0, 0.2, 1] }}
        className="summary-stats-grid"
      >
        {stats.map((stat, index) => {
          const card = (
            <div
              className={`summary-stat-card ${"tooltip" in stat && stat.tooltip ? "summary-stat-card--tooltip" : ""}`}
              style={{
                background: `linear-gradient(135deg, ${stat.accentColor}14 0%, ${stat.accentColor2}08 100%)`,
                border: `1px solid ${stat.accentColor}28`,
                borderLeft: `4px solid ${stat.accentColor}`,
                boxShadow: `0 4px 20px ${stat.accentColor}14`,
              }}
            >
              <div
                className="summary-stat-card-deco"
                style={{ background: `${stat.accentColor}08` }}
              />
              <div className="summary-stat-inner">
                <div className="summary-stat-content">
                  <p className="summary-stat-label" style={{ color: stat.accentColor }}>
                    {stat.title}
                  </p>
                  <p className="summary-stat-value">
                    {statsLoading ? (
                      <span className="summary-stat-value-loading">—</span>
                    ) : (
                      stat.value
                    )}
                  </p>
                  <p className="summary-stat-subtitle">{stat.subtitle}</p>
                </div>
                <div
                  className="summary-stat-icon-wrap"
                  style={{
                    background: `linear-gradient(135deg, ${stat.accentColor} 0%, ${stat.accentColor2} 100%)`,
                    boxShadow: `0 6px 18px ${stat.accentColor}40`,
                  }}
                >
                  {stat.icon}
                </div>
              </div>
            </div>
          );
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.35,
                delay: 0.1 + index * 0.08,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {"tooltip" in stat && stat.tooltip ? (
                <Tooltip title={stat.tooltip} placement="top">
                  {card}
                </Tooltip>
              ) : (
                card
              )}
            </motion.div>
          );
        })}
      </motion.div>

      {/* Aggregated Totals Card - shown when OEM/Category/Product filters are active */}
      {(selectedCategory || selectedOEM || selectedProduct) && statsData && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="summary-totals-card"
        >
          <Card bordered={false}>
            <div className="summary-totals-inner">
              <div className="summary-totals-icon-wrap">
                <BarChartOutlined />
              </div>
              <div className="summary-totals-text">
                <h3 className="summary-totals-title">Aggregated Totals (Filtered Results)</h3>
                <div className="summary-totals-row">
                  {statsData.totalPrice !== undefined && (
                    <div>
                      <span className="summary-totals-label">Total Price: </span>
                      <span className="summary-totals-number">
                        ₹{Number(statsData.totalPrice).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}
                  {statsData.finalPrice !== undefined && (
                    <div>
                      <span className="summary-totals-label">Final Price: </span>
                      <span className="summary-totals-number">
                        ₹{Number(statsData.finalPrice).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}
                  {statsData.totalQuantity !== undefined && (
                    <div>
                      <span className="summary-totals-label">Total Quantity: </span>
                      <span className="summary-totals-number">
                        {Number(statsData.totalQuantity).toLocaleString("en-IN")} units
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
      >
        <Card bordered={false} className="summary-table-card">
          <div className="summary-toolbar">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilter(true)}
              className={`summary-btn-filter ${activeFilterCount > 0 ? "summary-btn-filter--active" : "summary-btn-filter--inactive"}`}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="summary-badge-count summary-badge-count--accent">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setShowColumns(true)}
              className="summary-btn-columns"
            >
              Columns
              <span className="summary-badge-count summary-badge-count--purple">
                {selectedColumns.length}
              </span>
            </Button>
          </div>

          {listError && (
            <Alert
              type="error"
              message="Failed to load summary"
              description={
                listErrorDetail && typeof listErrorDetail === "object" && "data" in listErrorDetail
                  ? (listErrorDetail.data as { message?: string })?.message ?? "Please try again."
                  : "Please try again."
              }
              showIcon
              closable
              style={{ margin: 16 }}
              action={
                <Button size="small" onClick={() => refetchList()}>
                  Retry
                </Button>
              }
            />
          )}

          <Table<SummaryRowResponse>
            columns={tableColumns}
            dataSource={dataSource}
            loading={listLoading}
            tableLayout="fixed"
            scroll={{ x: totalScrollX, y: "calc(100vh - 200px)" }}
            expandable={{
              expandedRowKeys: Array.from(expandedRows),
              onExpand: (expanded, record) => {
                const newExpanded = new Set(expandedRows);
                if (expanded) {
                  newExpanded.add(record.poId);
                } else {
                  newExpanded.delete(record.poId);
                }
                setExpandedRows(newExpanded);
              },
              expandedRowRender: (record) => {
                if (!record.dispatches || record.dispatches.length === 0) {
                  return (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="summary-empty-dispatches"
                    >
                      <div className="summary-empty-dispatches-icon">
                        <TruckOutlined />
                      </div>
                      <div className="summary-empty-dispatches-title">No dispatches yet</div>
                      <div className="summary-empty-dispatches-desc">
                        Dispatch records will appear here once created
                      </div>
                    </motion.div>
                  );
                }

                const getDeliveryStatusStyle = (status: string): React.CSSProperties => {
                  const s = status.toLowerCase();
                  if (s.includes("delivered") || s.includes("done") || s.includes("complet")) {
                    return { background: "#11998e14", border: "1px solid #11998e35", color: "#065f46" };
                  }
                  if (s.includes("pending") || s.includes("partial")) {
                    return { background: "#f7971e14", border: "1px solid #f7971e35", color: "#92400e" };
                  }
                  return { background: "#667eea14", border: "1px solid #667eea35", color: "#4c51bf" };
                };

                return (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="summary-expanded-wrap"
                  >
                    <div className="summary-expanded-header">
                      <div className="summary-dispatch-badge">
                        <TruckOutlined />
                        <span>Dispatch Details</span>
                      </div>
                      <span className="summary-dispatch-count-pill">
                        {record.dispatches.length}{" "}
                        {record.dispatches.length === 1 ? "dispatch" : "dispatches"}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        className="summary-view-details-btn"
                        onClick={() => {
                          setSelectedPO(record);
                          setShowDetailModal(true);
                        }}
                      >
                        View Full Details →
                      </Button>
                    </div>

                    <div className="summary-expanded-list summary-expanded-list--dispatch-cards">
                      {record.dispatches.map((dispatch, idx) => {
                        const hasInvoiceInfo =
                          dispatch.taxInvoiceNumber || dispatch.invoiceDate || dispatch.dispatchDate;
                        const hasDeliveryInfo =
                          dispatch.deliveryLocation || dispatch.deliveryPincode;
                        const dispatchServices =
                          record.services?.filter((s) => s.dispatchId === dispatch.dispatchId) ?? [];
                        const serialListText = dispatchServices.map((s) => s.serialNumber).join(", ");

                        return (
                          <motion.div
                            key={dispatch.dispatchId}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            className="summary-dispatch-card"
                          >
                            <div className="summary-dispatch-card-header">
                              <div className="summary-dispatch-card-header-left">
                                <div className="summary-dispatch-card-icon">
                                  <TruckOutlined />
                                </div>
                                <div>
                                  <div className="summary-dispatch-card-title">
                                    Dispatch #{dispatch.dispatchId}
                                  </div>
                                  {dispatch.deliveryStatus && (
                                    <span
                                      className="summary-delivery-badge"
                                      style={getDeliveryStatusStyle(dispatch.deliveryStatus)}
                                    >
                                      {formatLabel(dispatch.deliveryStatus)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="summary-dispatch-qty-wrap">
                                <span className="summary-dispatch-qty-value">
                                  {dispatch.dispatchedQuantity.toLocaleString("en-IN")}
                                </span>
                                <span className="summary-dispatch-qty-unit">units</span>
                              </div>
                            </div>

                            <div className="summary-dispatch-card-body">
                              <div className="summary-dispatch-card-body-main">
                                {hasInvoiceInfo && (
                                  <div className="summary-dispatch-card-panel">
                                    <div className="summary-section-label summary-section-label--invoice">
                                      <FileTextOutlined />
                                      <span>Invoice Info</span>
                                    </div>
                                    <div className="summary-field-grid summary-field-grid--dispatch-cols">
                                      {dispatch.taxInvoiceNumber && (
                                        <div>
                                          <div className="summary-field-label">Tax Invoice</div>
                                          <div className="summary-field-value summary-field-value--accent">
                                            {dispatch.taxInvoiceNumber}
                                          </div>
                                        </div>
                                      )}
                                      {dispatch.invoiceDate && (
                                        <div>
                                          <div className="summary-field-label">Invoice Date</div>
                                          <div className="summary-field-value">
                                            {moment(dispatch.invoiceDate).format("DD MMM YYYY")}
                                          </div>
                                        </div>
                                      )}
                                      {dispatch.dispatchDate && (
                                        <div>
                                          <div className="summary-field-label">Dispatch Date</div>
                                          <div className="summary-field-value">
                                            {moment(dispatch.dispatchDate).format("DD MMM YYYY")}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {hasDeliveryInfo && (
                                  <div className="summary-dispatch-card-panel">
                                    <div className="summary-section-label summary-section-label--delivery">
                                      <ShoppingOutlined />
                                      <span>Delivery Info</span>
                                    </div>
                                    <div className="summary-field-grid summary-field-grid--dispatch-cols">
                                      {dispatch.deliveryLocation && (
                                        <div className="summary-dispatch-field-wide">
                                          <div className="summary-field-label">Location</div>
                                          <div className="summary-field-value summary-dispatch-address-value">
                                            {dispatch.deliveryLocation}
                                          </div>
                                        </div>
                                      )}
                                      {dispatch.deliveryPincode && (
                                        <div>
                                          <div className="summary-field-label">Material dispatch from</div>
                                          <div className="summary-field-value">{dispatch.deliveryPincode}</div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {dispatchServices.length > 0 && (
                                <div className="summary-dispatch-serials-section">
                                  <div className="summary-section-label summary-section-label--services">
                                    <ToolOutlined />
                                    <span>Serials ({dispatchServices.length})</span>
                                  </div>
                                  <p className="summary-dispatch-serial-comma" title={serialListText}>
                                    {serialListText}
                                  </p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              },
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: listData?.pagination?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size ?? 10);
              },
            }}
            locale={{
              emptyText: (
                <div className="summary-table-empty">
                  <div className="summary-table-empty-icon">
                    <FileSearchOutlined />
                  </div>
                  <p className="summary-table-empty-title">No orders found</p>
                  <p className="summary-table-empty-desc">
                    Adjust filters or create orders to see them here
                  </p>
                </div>
              ),
            }}
          />
        </Card>
      </motion.div>

      <AnimatePresence>
        {showFilter && (
          <SidePanel
            title="Filters"
            onClose={() => setShowFilter(false)}
            content={
              <div className="summary-filter-layout">
                {activeFilterCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="summary-active-filters"
                  >
                    <div className="summary-active-filters-title">
                      <FilterOutlined />
                      Active Filters ({activeFilterCount})
                    </div>
                    <Space size={[8, 8]} wrap>
                      {startDate && (
                        <Tag
                          closable
                          onClose={() => setStartDate(null)}
                          className="summary-filter-tag"
                        >
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          From: {startDate.format("DD-MM-YYYY")}
                        </Tag>
                      )}
                      {endDate && (
                        <Tag
                          closable
                          onClose={() => setEndDate(null)}
                          className="summary-filter-tag"
                        >
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          To: {endDate.format("DD-MM-YYYY")}
                        </Tag>
                      )}
                      {selectedCategory && (
                        <Tag
                          closable
                          onClose={() => {
                            setSelectedCategory(undefined);
                            setSelectedOEM(undefined);
                            setSelectedProduct(undefined);
                          }}
                          className="summary-filter-tag"
                        >
                          <ShoppingOutlined style={{ marginRight: 4 }} />
                          Category: {categoryOptions.find((c) => c.value === selectedCategory)?.label}
                        </Tag>
                      )}
                      {selectedOEM && (
                        <Tag
                          closable
                          onClose={() => {
                            setSelectedOEM(undefined);
                            setSelectedProduct(undefined);
                          }}
                          className="summary-filter-tag"
                        >
                          <ShoppingOutlined style={{ marginRight: 4 }} />
                          OEM: {oemOptions.find((o) => o.value === selectedOEM)?.label}
                        </Tag>
                      )}
                      {selectedProduct && (
                        <Tag
                          closable
                          onClose={() => setSelectedProduct(undefined)}
                          className="summary-filter-tag"
                        >
                          <ShoppingOutlined style={{ marginRight: 4 }} />
                          Product: {productOptions.find((p) => p.value === selectedProduct)?.label}
                        </Tag>
                      )}
                      {selectedClient && (
                        <Tag
                          closable
                          onClose={() => {
                            setSelectedClient(null);
                            setClientSearchTerm("");
                          }}
                          className="summary-filter-tag"
                        >
                          Customer: {selectedClient.clientName}
                        </Tag>
                      )}
                      {selectedSalesman && (
                        <Tag
                          closable
                          onClose={() => setSelectedSalesman(undefined)}
                          className="summary-filter-tag"
                        >
                          <UserOutlined style={{ marginRight: 4 }} />
                          Salesman: {salesmanOptions.find((s) => s.value === selectedSalesman)?.label}
                        </Tag>
                      )}
                      {poId && (
                        <Tag
                          closable
                          onClose={() => setPoId("")}
                          className="summary-filter-tag"
                        >
                          Order ID: {poId}
                        </Tag>
                      )}
                      {dispatchId && (
                        <Tag
                          closable
                          onClose={() => setDispatchId("")}
                          className="summary-filter-tag"
                        >
                          <TruckOutlined style={{ marginRight: 4 }} />
                          Dispatch: {dispatchId}
                        </Tag>
                      )}
                      {serviceId && (
                        <Tag
                          closable
                          onClose={() => setServiceId("")}
                          className="summary-filter-tag"
                        >
                          <ToolOutlined style={{ marginRight: 4 }} />
                          Service: {serviceId}
                        </Tag>
                      )}
                      {taxInvoice && (
                        <Tag
                          closable
                          onClose={() => setTaxInvoice("")}
                          className="summary-filter-tag"
                        >
                          Tax Invoice: {taxInvoice}
                        </Tag>
                      )}
                      {orderStatus && (
                        <Tag
                          closable
                          onClose={() => setOrderStatus(undefined)}
                          className="summary-filter-tag"
                        >
                          Status: {formatLabel(orderStatus)}
                        </Tag>
                      )}
                      {paymentStatus && (
                        <Tag
                          closable
                          onClose={() => setPaymentStatus(undefined)}
                          className="summary-filter-tag"
                        >
                          Payment: {formatLabel(paymentStatus)}
                        </Tag>
                      )}
                    </Space>
                  </motion.div>
                )}

                <div className="summary-filters-scroll">
                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <CalendarOutlined />
                      Time Period
                    </div>
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                      <DatePicker
                        placeholder="Start Date"
                        className="summary-filter-input"
                        style={{ width: "100%", borderRadius: 8, height: 40 }}
                        value={startDate}
                        onChange={setStartDate}
                        suffixIcon={<CalendarOutlined />}
                      />
                      <DatePicker
                        placeholder="End Date"
                        className="summary-filter-input"
                        style={{ width: "100%", borderRadius: 8, height: 40 }}
                        value={endDate}
                        onChange={setEndDate}
                        suffixIcon={<CalendarOutlined />}
                      />
                    </Space>
                  </Card>

                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <ShoppingOutlined />
                      Product Filters
                      <span className="summary-filter-hint">Category → OEM → Product</span>
                    </div>
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                      <div>
                        <div className="summary-filter-label">Category</div>
                        <Select
                          placeholder="Select Category"
                          className="summary-filter-input"
                          style={{ width: "100%", borderRadius: 8, height: 40 }}
                          value={selectedCategory}
                          onChange={(value) => {
                            setSelectedCategory(value);
                            setSelectedOEM(undefined);
                            setSelectedProduct(undefined);
                          }}
                          allowClear
                          options={categoryOptions}
                        />
                      </div>
                      <div>
                        <div className="summary-filter-label">OEM</div>
                        <Select
                          placeholder="Select OEM"
                          className="summary-filter-input"
                          style={{ width: "100%", borderRadius: 8, height: 40 }}
                          value={selectedOEM}
                          onChange={(value) => {
                            setSelectedOEM(value);
                            setSelectedProduct(undefined);
                          }}
                          allowClear
                          options={oemOptions}
                        />
                      </div>
                      <div>
                        <div className="summary-filter-label">Product</div>
                        <Select
                          placeholder={
                            !selectedCategory || !selectedOEM
                              ? "Select Category and OEM first"
                              : "Select Product"
                          }
                          style={{ width: "100%", borderRadius: 8, height: 40 }}
                          value={selectedProduct}
                          onChange={setSelectedProduct}
                          allowClear
                          disabled={!selectedCategory || !selectedOEM}
                          loading={productsLoading}
                          notFoundContent={
                            productsLoading ? (
                              <Spin size="small" />
                            ) : (
                              "No products found"
                            )
                          }
                          options={productOptions}
                        />
                      </div>
                    </Space>
                  </Card>

                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <FileTextOutlined />
                      Order Information
                    </div>
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                      <div>
                        <div className="summary-filter-label">Order ID</div>
                        <Input
                          placeholder="Enter PO ID"
                          value={poId}
                          onChange={(e) => setPoId(e.target.value)}
                          className="summary-filter-input"
                          style={{ borderRadius: 8, height: 40 }}
                        />
                      </div>
                      <div>
                        <div className="summary-filter-label">Customer</div>
                        <AutoComplete
                          options={clientOptions}
                          value={selectedClient?.clientName ?? clientSearchTerm}
                          onSelect={(value, option) => {
                            const opt = option as { clientId?: number; label?: string };
                            if (opt?.clientId != null) {
                              setSelectedClient({
                                clientId: opt.clientId,
                                clientName: value,
                              });
                              setClientSearchTerm(value);
                            }
                          }}
                          onSearch={(value) => {
                            setClientSearchTerm(value);
                            if (selectedClient && selectedClient.clientName !== value) {
                              setSelectedClient(null);
                            }
                          }}
                          placeholder="Type client name (min 3 characters)"
                          style={{ width: "100%", borderRadius: 8 }}
                          notFoundContent={
                            shouldFetchClients && clientOptions.length === 0
                              ? "No clients found"
                              : "Type at least 3 characters to search"
                          }
                        />
                      </div>
                    </Space>
                  </Card>

                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <UserOutlined />
                      Personnel
                    </div>
                    <div>
                      <div className="summary-filter-label">Salesman</div>
                      <Select
                        placeholder="Select Salesman"
                        className="summary-filter-input"
                        style={{ width: "100%", borderRadius: 8, height: 40 }}
                        value={selectedSalesman}
                        onChange={setSelectedSalesman}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        options={salesmanOptions}
                      />
                    </div>
                  </Card>

                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <TruckOutlined />
                      Dispatch Filters
                    </div>
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                      <div>
                        <div className="summary-filter-label">Dispatch ID</div>
                        <Input
                          placeholder="Enter Dispatch ID"
                          value={dispatchId}
                          onChange={(e) => setDispatchId(e.target.value)}
                          className="summary-filter-input"
                          style={{ borderRadius: 8, height: 40 }}
                        />
                      </div>
                      <div>
                        <div className="summary-filter-label">Tax Invoice</div>
                        <Input
                          placeholder="Enter Tax Invoice Number"
                          value={taxInvoice}
                          onChange={(e) => setTaxInvoice(e.target.value)}
                          className="summary-filter-input"
                          style={{ borderRadius: 8, height: 40 }}
                        />
                      </div>
                    </Space>
                  </Card>

                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <ToolOutlined />
                      Service Filters
                    </div>
                    <div>
                      <div className="summary-filter-label">Service ID</div>
                      <Input
                        placeholder="Enter Service ID"
                        value={serviceId}
                        onChange={(e) => setServiceId(e.target.value)}
                        className="summary-filter-input"
                        style={{ borderRadius: 8, height: 40 }}
                      />
                    </div>
                  </Card>

                  <Card size="small" className="summary-filter-section-card">
                    <div className="summary-filter-section-title">
                      <CheckCircleOutlined />
                      Status Filters
                    </div>
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                      <div>
                        <div className="summary-filter-label">Order Status</div>
                        <Select
                          placeholder="Select status"
                          className="summary-filter-input"
                          style={{ width: "100%", borderRadius: 8, height: 40 }}
                          value={orderStatus}
                          onChange={setOrderStatus}
                          allowClear
                          options={poStatusFilterOptions}
                        />
                      </div>
                      <div>
                        <div className="summary-filter-label">Payment Status</div>
                        <Select
                          placeholder="Select payment status"
                          className="summary-filter-input"
                          style={{ width: "100%", borderRadius: 8, height: 40 }}
                          value={paymentStatus}
                          onChange={setPaymentStatus}
                          allowClear
                          options={paymentStatusOptions}
                        />
                      </div>
                      <div>
                        <div className="summary-filter-label">Pipeline Stage</div>
                        <Space direction="vertical" style={{ width: "100%" }} size={8}>
                          <Select
                            placeholder="Stage"
                            className="summary-filter-input"
                            style={{ width: "100%", borderRadius: 8, height: 40 }}
                            value={pipelineStage}
                            onChange={setPipelineStage}
                            allowClear
                            options={PIPELINE_STAGES}
                          />
                          <Select
                            placeholder="Status"
                            className="summary-filter-input"
                            style={{ width: "100%", borderRadius: 8, height: 40 }}
                            value={pipelineStatus}
                            onChange={setPipelineStatus}
                            allowClear
                            options={PIPELINE_STATUS_OPTIONS}
                          />
                        </Space>
                      </div>
                    </Space>
                  </Card>
                </div>

                <div className="summary-filter-footer">
                  <Button block onClick={handleClearFilters} className="summary-btn-clear">
                    Clear All
                  </Button>
                  <Button
                    type="primary"
                    block
                    onClick={handleApplyFilters}
                    className="summary-btn-apply"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            }
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showColumns && (
          <SidePanel
            title="Manage Columns"
            onClose={() => setShowColumns(false)}
            content={
              <div className="summary-columns-layout">
                <div className="summary-columns-actions">
                  <Button
                    size="small"
                    onClick={() => setSelectedColumns([...ALL_COLUMN_KEYS])}
                    className="summary-columns-btn"
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedColumns([])}
                    className="summary-columns-btn"
                  >
                    Clear All
                  </Button>
                </div>
                <div className="summary-columns-scroll">
                  {ALL_COLUMN_KEYS.map((key) => (
                    <Checkbox
                      key={key}
                      checked={selectedColumns.includes(key)}
                      onChange={(e) =>
                        setSelectedColumns(
                          e.target.checked
                            ? [...selectedColumns, key]
                            : selectedColumns.filter((k) => k !== key)
                        )
                      }
                      className="summary-columns-checkbox"
                    >
                      {COLUMN_TITLES[key] ?? key}
                    </Checkbox>
                  ))}
                </div>
              </div>
            }
          />
        )}
      </AnimatePresence>

      <SummaryDetailModal
        visible={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedPO(null);
        }}
        po={selectedPO}
      />
    </div>
  );
};

interface SidePanelProps {
  title: string;
  onClose: () => void;
  content: React.ReactNode;
}

const SidePanel: React.FC<SidePanelProps> = ({ title, onClose, content }) => (
  <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="summary-sidepanel-overlay"
    />
    <motion.div
      initial={{ x: 450, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 450, opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="summary-sidepanel"
    >
      <div className="summary-sidepanel-header">
        <h3 className="summary-sidepanel-title">{title}</h3>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          className="summary-sidepanel-close"
        />
      </div>
      <div className="summary-sidepanel-body">{content}</div>
    </motion.div>
  </>
);

export default SummaryDashboard;
